import { supabase } from './supabase'
import { warnMissingFields } from '../utils/devAssert'

/**
 * @typedef {Object} ConProject
 * @property {string} id            — GHL opportunity ID
 * @property {string} name          — opportunity title
 * @property {string} clientName
 * @property {string} clientEmail
 * @property {string} clientPhone
 * @property {string} stageId       — GHL pipeline stage UUID
 * @property {string} pipelineId    — GHL pipeline UUID
 * @property {string} status        — 'open' | 'won' | 'lost' | 'abandoned'
 * @property {number} monetaryValue
 * @property {string} updatedAt
 * @property {string} contactId     — GHL contact ID (used for notes)
 */

const _CON_FIELDS = [
    'id', 'name', 'clientName', 'clientEmail', 'clientPhone',
    'stageId', 'pipelineId', 'status', 'monetaryValue', 'updatedAt', 'contactId',
    'createdAt',
]

/**
 * Calls the ghl-proxy Supabase Edge Function.
 * GHL private integration tokens never leave the server.
 */
export async function callGHLProxy(action, subAccount, payload = {}) {
  const { data, error } = await supabase.functions.invoke('ghl-proxy', {
    body: { action, subAccount, payload },
  })

  if (error) {
    let msg = error.message || 'GHL proxy error'
    try {
      if (error.context) {
        const body = await error.context.json()
        if (body?.error) msg = `[ghl-proxy:${action}/${subAccount}] ${body.error}`
      }
    } catch (_) { /* fall through to default msg */ }
    throw new Error(msg)
  }
  if (data?.error) throw new Error(`[ghl-proxy:${action}/${subAccount}] ${data.error}`)
  return data
}

// ── Sub-account routing config ──────────────────────────────────────────────
// Architecture sub-accounts (miami + dallas) share the same GHL location and pipelines:
//   Ventas pipeline (x17yrEgBY1WkrjKBf8Nr) — 16 stages, mirrors Estimates board labels 1–16
//   Producción pipeline (xxrZ4LFMCG4fJCY2FiKJ) — 40 stages, mirrors Projects board labels 1–40
// Stage IDs verified via GHL API on Jul 26, 2026 after UI restructure.

export const GHL_CONFIG = {
  dallas: {
    estimatesPipelineId: 'x17yrEgBY1WkrjKBf8Nr',
    estimatesStageIds: [
      '1beb6228-c3be-4f56-b418-1da542d4beb5', // 1. Prospecto
      '644d2da0-e611-4445-be4d-511ed09cddc9', // 2. Primera Llamada
      '06ea227e-7971-436f-8749-ecb5f00c6076', // 3. No Contestó
      'af60585d-e750-4d66-a436-6ab35411abad', // 4. Llamada Programada
      '86fa757d-c75f-4445-af7d-6d38ec1c101b', // 5. No Calificó
      'de774713-0f99-43a9-81f9-74210bf48a82', // 6. No Dio Información
      '8bf2d81f-1359-4620-9ee8-76a0f0134bf3', // 7. Solicitud de Estimado
      '53fd3035-6565-4172-80f4-ed0725114a6d', // 8. Revisión Inicial
      'cf365301-96f3-4ad7-90f8-621c7ecf1a82', // 9. Visita Requerida
      'fc4201fe-9354-4c10-b309-9dca10f817e1', // 10. En Cotización / Estimado
      '60ddde5f-2dbb-4c5c-8dad-bc196b2c23a5', // 11. Presentación de Venta
      '830a08a3-8d1c-43ad-b2c1-2143ff9146ab', // 12. En Cotización Formal
      '3d35eaad-94a4-4cc3-8c97-1d815c52650d', // 13. A Cierre de Venta
      'f4a34e8c-738c-416c-8854-a79262c9f25b', // 14. Descartado
      'a7f55173-0dfb-4576-a9ac-03ab73337239', // 15. Firma de Contrato
      'd1aa2467-d5e4-405e-8c4d-241dfe2a5dc3', // 16. Primer Pago
    ],
    projectsPipelineId: 'xxrZ4LFMCG4fJCY2FiKJ',
    projectsStageIds: [
      'c439d617-0f8e-450b-bcc9-42bc8274c6a4', // 1. PM Da la Bienvenida y Coord Visita
      'fa89c84d-4491-4a6d-9815-0228868476d4', // 2. PM Da la Bienvenida y/o En Prescreen
      '92298f8f-1388-4afc-8188-ece99992d6d5', // 3. Producción Preliminar
      '51c13789-d9b9-44cb-aee6-19a52b1fd0f3', // 4. PM Envía a Producción Preliminar
      'beef0a64-db9c-41f3-8cfd-9ab2b49cd530', // 5. En Revisión de Preliminar por PM
      'c2ef19f4-fa60-4d5e-af2f-b7e9cbc3a8c8', // 6. 1er Envío Preliminar a Cliente
      '7c98886a-1fe7-4ba3-9b70-c1b5e7cff679', // 7. Cliente Recibe para Aprobación (1ra)
      '30e9a98c-d6ff-49b2-ae17-b47402d76059', // 8. PM Recibe Aprobación Preliminar (1ra)
      'b8c8b4dd-63a1-43d4-bc91-24e9ea564150', // 9. PM Recibe Correcciones Preliminar (1ra)
      '4b91455f-3856-40bb-9c22-bbbf29f38e45', // 10. En Corrección Preliminar (2da)
      'f6fa27be-32c6-438f-9da5-6fd1df3bc000', // 11. 2do Envío Preliminar a Cliente
      '9f728e71-f4ea-49f1-940e-2ae790f7a1ea', // 12. Cliente Recibe para Aprobación (2da)
      '75e517d5-e213-4e2a-87ac-5e59c02edc70', // 13. PM Recibe Aprobación Preliminar (2da)
      '2d38639a-a1a1-4de6-a13b-6170ac423daf', // 14. PM Recibe Correcciones Preliminar (2da)
      'db598b65-87b0-44d3-9ff6-56f5c5a4e0f0', // 15. En Corrección Preliminar (3ra)
      '931605ba-02d2-48c5-9b92-95aef194c398', // 16. 3er Envío Preliminar a Cliente
      '5051e61b-6710-4035-b051-81e091b2297f', // 17. Cliente Recibe para Aprobación (3ra)
      '89672135-44c1-437c-8cc8-417f19a08454', // 18. PM Recibe Aprobación Preliminar (3ra)
      '409d9fd8-f51f-42ed-99d6-0ad549f9c896', // 19. Solicitud de 2do Pago a Cliente
      '925024c1-3077-4c29-87a5-81982804d149', // 20. PM Da Instrucciones de CD (1ra)
      '8ff0ec8a-3975-41f8-99f3-fb366f0587d8', // 21. En Producción por Proyectistas (1ra)
      '120f9453-4fd5-4a7b-b28c-3c29916d6967', // 22. En 1era Revisión PM de CD
      '8c5a0e0e-953b-4c88-91d6-ad20867ca4fb', // 23. Revisión de Cada Disciplina / Certificación (1ra)
      '043059cd-b0d4-4b2c-9457-c5fdb6d76163', // 24. PM Envió Preliminar CD al Cliente
      '62ebb421-0624-4835-af6e-0eb2535d4f77', // 25. PM Recibe Aprobación de Cliente CD
      '5f1a4901-d0f4-46f8-954c-1d3348bfa6eb', // 26. PM Recibe Correcciones de Cliente CD
      'ef1cf49f-d98a-4110-b66b-789210877b1a', // 27. PM Da Instrucciones de CD (2da)
      '16680f41-b4aa-4fee-9ac9-dffc356de7e2', // 28. En Producción por Proyectistas (2da)
      'e9beefac-f1f3-4857-bb83-4964a510dee4', // 29. 2da Revisión PM de CD
      'cf92ef13-9ed8-4257-9608-a8dfbdc66030', // 30. 2da Revisión de Certificación
      'cf9d007a-d5ce-4f64-8e89-0d38755e8bd5', // 31. PM 2do Envío Preliminar CD al Cliente
      'e6a09410-43eb-49ba-af2b-d5a31e534948', // 32. Cliente Aprueba CD
      '430bd865-86b3-4f58-849a-a4d5b055ba48', // 33. PM Revisa y Solicita 3er Pago CD
      'baf1c386-705a-498a-b324-8be8b6f78bd1', // 34. Cliente Paga CD
      'c4be750b-6ac1-4a2b-a491-554b75da9b14', // 35. PM Envía a Certificación Final
      '37359599-c86e-44e5-94c8-264b16b11cd2', // 36. En Certificación Final
      '07e7ea32-53bf-4763-a341-796a03dfd498', // 37. PM Recibe Certificación Final / Control de Calidad
      '428204b0-4898-4865-bb98-f615144524c2', // 38. PM Empaqueta y Envía
      'a7e762cb-f36e-426f-8b9b-c32dfdd6224a', // 39. Cliente Recibe Conforme y/o Aprueba Someter a la Ciudad
      '43f67231-9939-48fe-8181-9293a6d5c8a2', // 40. Suspendido
    ],
    pipelineId: 'x17yrEgBY1WkrjKBf8Nr',
    enConsultaStageId: '1beb6228-c3be-4f56-b418-1da542d4beb5',
    customFieldIds: {
      Project_Address: 'VlJMqxvu2eXqX9TdaAQV',
      Project_Name:    '4VUWyZjzlDnzJf2T9v2R',
      Project_Type:    'rwgoMUocxd3cPGsIPcdB',
      Company_Name:    'yJUTvzTyHAEVYZTa4Isj',
      Legal_Rep:       'RnoFYbQEhZTQMDQrYdcK',
    },
  },
  // JD Rivero Architecture & Engineering (formerly JD Rivero - Miami LLC)
  // Location ID and API key unchanged — only the GHL display name changed.
  // Receives ALL arch form submissions: FL address → tagged 'miami'; TX address → tagged 'dallas'.
  miami: {
    estimatesPipelineId: 'x17yrEgBY1WkrjKBf8Nr',
    estimatesStageIds: [
      '1beb6228-c3be-4f56-b418-1da542d4beb5', // 1. Prospecto
      '644d2da0-e611-4445-be4d-511ed09cddc9', // 2. Primera Llamada
      '06ea227e-7971-436f-8749-ecb5f00c6076', // 3. No Contestó
      'af60585d-e750-4d66-a436-6ab35411abad', // 4. Llamada Programada
      '86fa757d-c75f-4445-af7d-6d38ec1c101b', // 5. No Calificó
      'de774713-0f99-43a9-81f9-74210bf48a82', // 6. No Dio Información
      '8bf2d81f-1359-4620-9ee8-76a0f0134bf3', // 7. Solicitud de Estimado
      '53fd3035-6565-4172-80f4-ed0725114a6d', // 8. Revisión Inicial
      'cf365301-96f3-4ad7-90f8-621c7ecf1a82', // 9. Visita Requerida
      'fc4201fe-9354-4c10-b309-9dca10f817e1', // 10. En Cotización / Estimado
      '60ddde5f-2dbb-4c5c-8dad-bc196b2c23a5', // 11. Presentación de Venta
      '830a08a3-8d1c-43ad-b2c1-2143ff9146ab', // 12. En Cotización Formal
      '3d35eaad-94a4-4cc3-8c97-1d815c52650d', // 13. A Cierre de Venta
      'f4a34e8c-738c-416c-8854-a79262c9f25b', // 14. Descartado
      'a7f55173-0dfb-4576-a9ac-03ab73337239', // 15. Firma de Contrato
      'd1aa2467-d5e4-405e-8c4d-241dfe2a5dc3', // 16. Primer Pago
    ],
    projectsPipelineId: 'xxrZ4LFMCG4fJCY2FiKJ',
    projectsStageIds: [
      'c439d617-0f8e-450b-bcc9-42bc8274c6a4', // 1. PM Da la Bienvenida y Coord Visita
      'fa89c84d-4491-4a6d-9815-0228868476d4', // 2. PM Da la Bienvenida y/o En Prescreen
      '92298f8f-1388-4afc-8188-ece99992d6d5', // 3. Producción Preliminar
      '51c13789-d9b9-44cb-aee6-19a52b1fd0f3', // 4. PM Envía a Producción Preliminar
      'beef0a64-db9c-41f3-8cfd-9ab2b49cd530', // 5. En Revisión de Preliminar por PM
      'c2ef19f4-fa60-4d5e-af2f-b7e9cbc3a8c8', // 6. 1er Envío Preliminar a Cliente
      '7c98886a-1fe7-4ba3-9b70-c1b5e7cff679', // 7. Cliente Recibe para Aprobación (1ra)
      '30e9a98c-d6ff-49b2-ae17-b47402d76059', // 8. PM Recibe Aprobación Preliminar (1ra)
      'b8c8b4dd-63a1-43d4-bc91-24e9ea564150', // 9. PM Recibe Correcciones Preliminar (1ra)
      '4b91455f-3856-40bb-9c22-bbbf29f38e45', // 10. En Corrección Preliminar (2da)
      'f6fa27be-32c6-438f-9da5-6fd1df3bc000', // 11. 2do Envío Preliminar a Cliente
      '9f728e71-f4ea-49f1-940e-2ae790f7a1ea', // 12. Cliente Recibe para Aprobación (2da)
      '75e517d5-e213-4e2a-87ac-5e59c02edc70', // 13. PM Recibe Aprobación Preliminar (2da)
      '2d38639a-a1a1-4de6-a13b-6170ac423daf', // 14. PM Recibe Correcciones Preliminar (2da)
      'db598b65-87b0-44d3-9ff6-56f5c5a4e0f0', // 15. En Corrección Preliminar (3ra)
      '931605ba-02d2-48c5-9b92-95aef194c398', // 16. 3er Envío Preliminar a Cliente
      '5051e61b-6710-4035-b051-81e091b2297f', // 17. Cliente Recibe para Aprobación (3ra)
      '89672135-44c1-437c-8cc8-417f19a08454', // 18. PM Recibe Aprobación Preliminar (3ra)
      '409d9fd8-f51f-42ed-99d6-0ad549f9c896', // 19. Solicitud de 2do Pago a Cliente
      '925024c1-3077-4c29-87a5-81982804d149', // 20. PM Da Instrucciones de CD (1ra)
      '8ff0ec8a-3975-41f8-99f3-fb366f0587d8', // 21. En Producción por Proyectistas (1ra)
      '120f9453-4fd5-4a7b-b28c-3c29916d6967', // 22. En 1era Revisión PM de CD
      '8c5a0e0e-953b-4c88-91d6-ad20867ca4fb', // 23. Revisión de Cada Disciplina / Certificación (1ra)
      '043059cd-b0d4-4b2c-9457-c5fdb6d76163', // 24. PM Envió Preliminar CD al Cliente
      '62ebb421-0624-4835-af6e-0eb2535d4f77', // 25. PM Recibe Aprobación de Cliente CD
      '5f1a4901-d0f4-46f8-954c-1d3348bfa6eb', // 26. PM Recibe Correcciones de Cliente CD
      'ef1cf49f-d98a-4110-b66b-789210877b1a', // 27. PM Da Instrucciones de CD (2da)
      '16680f41-b4aa-4fee-9ac9-dffc356de7e2', // 28. En Producción por Proyectistas (2da)
      'e9beefac-f1f3-4857-bb83-4964a510dee4', // 29. 2da Revisión PM de CD
      'cf92ef13-9ed8-4257-9608-a8dfbdc66030', // 30. 2da Revisión de Certificación
      'cf9d007a-d5ce-4f64-8e89-0d38755e8bd5', // 31. PM 2do Envío Preliminar CD al Cliente
      'e6a09410-43eb-49ba-af2b-d5a31e534948', // 32. Cliente Aprueba CD
      '430bd865-86b3-4f58-849a-a4d5b055ba48', // 33. PM Revisa y Solicita 3er Pago CD
      'baf1c386-705a-498a-b324-8be8b6f78bd1', // 34. Cliente Paga CD
      'c4be750b-6ac1-4a2b-a491-554b75da9b14', // 35. PM Envía a Certificación Final
      '37359599-c86e-44e5-94c8-264b16b11cd2', // 36. En Certificación Final
      '07e7ea32-53bf-4763-a341-796a03dfd498', // 37. PM Recibe Certificación Final / Control de Calidad
      '428204b0-4898-4865-bb98-f615144524c2', // 38. PM Empaqueta y Envía
      'a7e762cb-f36e-426f-8b9b-c32dfdd6224a', // 39. Cliente Recibe Conforme y/o Aprueba Someter a la Ciudad
      '43f67231-9939-48fe-8181-9293a6d5c8a2', // 40. Suspendido
    ],
    pipelineId: 'x17yrEgBY1WkrjKBf8Nr',
    enConsultaStageId: '1beb6228-c3be-4f56-b418-1da542d4beb5',
    customFieldIds: {
      Project_Address: 'Qp58lNMaDcx9nIJktBpO',
      Project_Name:    'B040VZMTdhp4XuiEU6UU',
      Project_Type:    '8nVtulAKbyeWcnm6tOc4',
      Company_Name:    'IxneCtWe6I3hrp9nfVCm',
      Legal_Rep:       'CJYCg6UirpFTbwngdsix',
    },
  },
  construction: {
    pipelineId: 'p2ZEjMort6BjUO1g2RJN',       // Pipeline: Ventas
    enConsultaStageId: 'c4090225-5af6-41b9-89f4-fb4c56fde029', // Stage: 1.1. LEAD NUEVO (V)
    customFieldIds: {
      Project_Address: 'fw02rFnNW2820x356HAj',
      Project_Name:    '6KJsbXXyVZQ4OmHmYqXa',
      Project_Type:    'G8lZkoaWQpUOSLfIwiQ2',
      Company_Name:    '4W1390WGBUjFOzQWmx1p',
      Legal_Rep:       'foSFj6trcnj70iM2jwCW',
    },
  },
}

// ── Tag resolver ──────────────────────────────────────────────────────────────

// Florida indicators → 'miami' tag; Texas indicators → 'dallas' tag
const STATE_PATTERNS = [
  { tag: 'miami',  re: /\bflorida\b|\bFL\b/i },
  { tag: 'dallas', re: /\btexas\b|\bTX\b/i },
]

// Construction Description (color_mktcap3t) → tag
const DESCRIPTION_TAG_MAP = {
  'Nueva construcción': 'new-cons',
  'Addition':           'addition',
  'Remodel':            'remodel',
  'Re-Roof':            'reroof',
  'Windows':            'windows',
  'Pergola':            'pergola',
  'Concrete':           'concrete',
  'Cerca':              'fence',
}

// Construction Applicant Type (color_mm1gktv9) → tag
const APPLICANT_TAG_MAP = {
  'Cliente':            'client',
  'Equipo de ventas':   'sales-team',
}

// Landing form → auto-assign opportunities to Andres Haddad (Construction sub-account)
const LANDING_ASSIGNED_USER = 'QmiC5ocvSRgQeWa13L9w'

// Landing form timeline → lead temperature tag
const TIMELINE_TAG_MAP = {
  'asap':      'L30',
  '1-3months': 'L20',
  'exploring': 'L10',
}

/**
 * Determine contact tags for a form submission.
 *
 * - Construction / Roofing / Impact Windows → ['web-form', <description-tag>, <applicant-tag>]
 * - Architecture → [<geo-tag>] based on address state parsing
 * @returns {string[]} array of tags (may be empty)
 */
function resolveTags(formType, formData, utmData) {
  if (formType === 'construction_new_project' || formType === 'roofing_new_project' || formType === 'impact_windows_new_project') {
    const tags = ['web-form']
    const desc = formData.color_mktcap3t
    if (desc && DESCRIPTION_TAG_MAP[desc]) tags.push(DESCRIPTION_TAG_MAP[desc])
    const applicant = formData.color_mm1gktv9
    if (applicant && APPLICANT_TAG_MAP[applicant]) tags.push(APPLICANT_TAG_MAP[applicant])
    const timelineTag = TIMELINE_TAG_MAP[formData._landing_timeline]
    if (timelineTag) tags.push(timelineTag)
    // UTM-derived tags (Meta Ads attribution)
    if (utmData) {
      if (utmData.utm_source) tags.push(utmData.utm_source)
      if (utmData.utm_content) tags.push(utmData.utm_content.toLowerCase().replace(/\s+/g, '-'))
      if (utmData.utm_term) tags.push(utmData.utm_term.toLowerCase().replace(/\s+/g, '-'))
    }
    return tags
  }

  if (formType === 'estimados_2026_new_app' || formType === 'estimados_2026_new_scope') {
    const haystack = `${formData.name || ''} ${formData.location_mkt69c7k || ''}`
    for (const { tag, re } of STATE_PATTERNS) {
      if (re.test(haystack)) return [tag]
    }
    return [] // no state detected → skip tagging
  }

  return []
}

// ── Health check ────────────────────────────────────────────────────────────

/** Ping a GHL sub-account to verify connectivity. Defaults to 'construction'. */
export async function pingGHL(subAccount = 'construction') {
  return callGHLProxy('ping', subAccount)
}

// ── Contact helpers ─────────────────────────────────────────────────────────

/**
 * Search for an existing contact in a sub-account by phone first, then email.
 * Returns the first match or null.
 */
export async function searchGHLContact(subAccount, { phone, email }) {
  // 1. Search by phone (stripped digits)
  if (phone) {
    const stripped = phone.replace(/\D/g, '')
    if (stripped.length >= 7) {
      try {
        const res = await callGHLProxy('search_contact', subAccount, {
          query: stripped,
          field: 'phone',
        })
        if (res.contact) return res.contact
      } catch (err) {
        console.warn('[GHL] Phone search failed:', err.message)
      }
    }
  }

  // 2. Fallback: search by email
  if (email) {
    try {
      const res = await callGHLProxy('search_contact', subAccount, {
        query: email,
        field: 'email',
      })
      if (res.contact) return res.contact
    } catch (err) {
      console.warn('[GHL] Email search failed:', err.message)
    }
  }

  return null
}

/** Create a new contact in a GHL sub-account */
export async function createGHLContact(subAccount, { firstName, lastName, phone, email }) {
  const res = await callGHLProxy('create_contact', subAccount, {
    firstName,
    lastName,
    phone,
    email,
  })
  return res.contact
}

// ── Opportunity helpers ─────────────────────────────────────────────────────

/** Get all opportunities for a contact in a sub-account */
export async function getContactOpportunities(subAccount, contactId) {
  const res = await callGHLProxy('search_opportunities', subAccount, { contactId })
  return res.opportunities || []
}

/** Create a new opportunity in a sub-account */
export async function createGHLOpportunity(subAccount, { title, pipelineId, stageId, contactId, customFields, assignedTo }) {
  const payload = {
    title,
    pipelineId,
    stageId,
    contactId,
    customFields,
  }
  if (assignedTo) payload.assignedTo = assignedTo
  const res = await callGHLProxy('create_opportunity', subAccount, payload)
  return res.opportunity
}

/** Update an existing opportunity — preserves stage and status */
export async function updateGHLOpportunity(subAccount, { opportunityId, title, customFields }) {
  const res = await callGHLProxy('update_opportunity', subAccount, {
    opportunityId,
    title,
    customFields,
  })
  return res.opportunity
}

/** Update a contact's name */
export async function updateGHLContact(subAccount, contactId, { firstName, lastName }) {
  return callGHLProxy('update_contact', subAccount, { contactId, firstName, lastName })
}

/** Append tags to a contact */
export async function tagGHLContact(subAccount, contactId, tags) {
  return callGHLProxy('update_contact_tags', subAccount, { contactId, tags })
}

/** Create a note on a contact */
export async function createGHLContactNote(subAccount, contactId, body) {
  return callGHLProxy('create_contact_note', subAccount, { contactId, body })
}

// ── Client Comment (Workflow) ────────────────────────────────────────────────

/**
 * Sanitize user input: strip HTML tags and limit length.
 * Keeps plain text safe for GHL note body.
 */
function sanitizeComment(raw, maxLen = 1000) {
  return raw
    .replace(/<[^>]*>/g, '')          // strip HTML tags
    .replace(/[<>"'&]/g, '')          // strip remaining dangerous chars
    .trim()
    .slice(0, maxLen)
}

/**
 * Post a client comment to the matching GHL contact's note.
 *
 * Uses the project's board ID to deterministically resolve the correct
 * GHL sub-account — no guessing or multi-account scanning needed.
 *
 * Dallas + Miami sub-accounts are now merged into Architecture & Engineering (miami).
 * Board ID lookup routes all arch boards to the single 'miami' sub-account.
 *
 * @param {string} email       - client email (from Monday project)
 * @param {string} address     - project address / Monday item name
 * @param {string} comment     - raw comment text (will be sanitized)
 * @param {string} boardId     - Monday board ID → resolves GHL sub-account
 * @returns {Promise<object>}  - GHL note response
 */

// Monday board → GHL sub-account mapping
// Both arch boards now route to 'miami' (JD Rivero Architecture & Engineering).
const BOARD_TO_SUB_ACCOUNT = {
  '18396594554': 'miami',       // Project Miami 2026  → Architecture & Engineering
  '18396592406': 'miami',       // Project Dallas 2026 → Architecture & Engineering (TX contacts tagged 'dallas')
}

export async function postClientComment(email, address, comment, boardId) {
  const safe = sanitizeComment(comment)
  if (!safe) throw new Error('Comment is empty after sanitization')

  // Resolve sub-account from board ID (deterministic)
  const subAccount = BOARD_TO_SUB_ACCOUNT[boardId]
  if (!subAccount) {
    console.warn(`[GHL] Unknown boardId "${boardId}" — cannot resolve sub-account`)
    throw new Error('Cannot determine GHL sub-account for this project')
  }

  // Find contact by email in the correct sub-account
  const contact = await searchGHLContact(subAccount, { email })
  if (!contact?.id) throw new Error(`GHL contact not found in ${subAccount} for ${email}`)

  // Find matching opportunity by address
  const opportunities = await getContactOpportunities(subAccount, contact.id)
  const match = opportunities.find(o =>
    (o.name || '').toLowerCase().includes(address.toLowerCase())
  )

  // Create note on contact, tagged with project address
  const prefix = match ? `[${address}]` : `[${address} — no opportunity match]`
  const body = `Client: ${prefix} ${safe}`
  return createGHLContactNote(subAccount, contact.id, body)
}

// ── Construction workflow comments (GHL contact notes) ───────────────────────

export async function fetchGHLContactNotes(contactId, limit = 5) {
  const data = await callGHLProxy('get_contact_notes', 'construction', { contactId, limit })
  return data?.notes || []
}

export async function postGHLContactNote(contactId, text) {
  return callGHLProxy('create_contact_note', 'construction', { contactId, body: text })
}

// ── Project Type resolver ────────────────────────────────────────────────────

/**
 * Map Monday color_mktck81k raw value → GHL Project_Type dropdown label.
 * Construction board indices: 0=Comercial, 1=Residencial, Civil=text
 * Architecture board indices: 0=Civil, 1=Comercial, 2=Residencial
 * GHL dropdown values: Res, Com, Civil
 */
function resolveGHLProjectType(formType, rawValue) {
  if (['construction_new_project', 'roofing_new_project', 'impact_windows_new_project'].includes(formType)) {
    const map = { '0': 'Com', '1': 'Res', 'Civil': 'Civil' }
    return map[rawValue] || rawValue
  }
  if (['estimados_2026_new_app', 'estimados_2026_new_scope'].includes(formType)) {
    const map = { '0': 'Civil', '1': 'Com', '2': 'Res' }
    return map[rawValue] || rawValue
  }
  return rawValue
}

// ── Orchestrator ────────────────────────────────────────────────────────────

/**
 * Resolve which GHL sub-account to target based on form type and office field.
 *
 * @param {string} formType - WORK_FORMS key
 * @param {object} formData - raw form data
 * @returns {string|null} 'miami' | 'construction' | null
 */
function resolveSubAccount(formType, formData) {
  if (formType === 'construction_new_project' || formType === 'roofing_new_project' || formType === 'impact_windows_new_project') {
    return 'construction'
  }

  if (formType === 'estimados_2026_new_app' || formType === 'estimados_2026_new_scope') {
    // All arch forms route to Architecture & Engineering (miami), regardless of office selection.
    // TX addresses receive a 'dallas' tag automatically via STATE_PATTERNS in resolveTags().
    return 'miami'
  }

  return null
}

/**
 * Full GHL sync orchestrator. Called after a successful Monday.com form submission.
 * Fire-and-forget — errors are logged but never thrown to callers.
 *
 * @param {object} params
 * @param {string} params.formType - WORK_FORMS key
 * @param {object} params.formData - raw form data from the form
 */
export async function syncFormToGHL({ formType, formData, scopeText = '', utmData = null }) {
  try {
    // 1. Resolve sub-account
    const subAccount = resolveSubAccount(formType, formData)
    if (!subAccount) {
      console.log('[GHL] No sub-account resolved for form type:', formType)
      return
    }

    const config = GHL_CONFIG[subAccount]
    if (!config) {
      console.warn('[GHL] No config for sub-account:', subAccount)
      return
    }

    // 2. Extract form fields
    const phone = formData.phone_mkt9n841 || ''
    const email = formData.text_mkt9qv82 || ''
    const clientName = formData.text_mkt9adtr || ''
    const projectAddress = formData.name || ''
    const projectType = formData.color_mktck81k

    if (!phone && !email) {
      console.log('[GHL] Skipping — no phone or email provided')
      return
    }

    // 3. Contact dedup: search by phone then email
    let contact = await searchGHLContact(subAccount, { phone, email })

    const nameParts = clientName.trim().split(/\s+/)
    const firstName = nameParts[0] || ''
    const lastName = nameParts.slice(1).join(' ') || ''

    // 4. If no contact found, create one; otherwise update name to match form
    if (!contact) {
      console.log(`[GHL] Creating new contact in ${subAccount}: ${firstName} ${lastName}`)
      contact = await createGHLContact(subAccount, {
        firstName,
        lastName,
        phone: phone || undefined,
        email: email || undefined,
      })
    } else if (contact.firstName !== firstName || contact.lastName !== lastName) {
      console.log(`[GHL] Updating contact name: "${contact.firstName} ${contact.lastName}" → "${firstName} ${lastName}"`)
      await updateGHLContact(subAccount, contact.id, { firstName, lastName }).catch(e =>
        console.warn('[GHL] Contact name update failed:', e.message)
      )
    }

    if (!contact?.id) {
      console.warn('[GHL] Failed to resolve or create contact')
      return
    }

    console.log(`[GHL] Contact resolved: ${contact.id} (${contact.name || contact.firstName})`)

    // 5. Build title + custom fields
    const optionSub = formData._option_subcategory || ''
    const timelineTag = TIMELINE_TAG_MAP[formData._landing_timeline] || ''
    const nameWithTag = timelineTag ? `${clientName} [${timelineTag}]` : clientName
    const title = optionSub
      ? `${nameWithTag} - ${optionSub} - ${projectAddress}`
      : `${nameWithTag} - ${projectAddress}`
    const customFields = [
      { id: config.customFieldIds.Project_Address, field_value: projectAddress },
      { id: config.customFieldIds.Project_Name, field_value: clientName },
    ]

    // Optional company / legal-rep fields — column IDs differ per Monday board
    const companyName = formData.text_mktg6f8x || formData.text_mm8kdbqf || ''
    const legalRep = formData.text_mm8kasxy || formData.text_mm8k828a || ''
    if (companyName && config.customFieldIds.Company_Name) {
      customFields.push({ id: config.customFieldIds.Company_Name, field_value: companyName })
    }
    if (legalRep && config.customFieldIds.Legal_Rep) {
      customFields.push({ id: config.customFieldIds.Legal_Rep, field_value: legalRep })
    }

    if (projectType) {
      const typeLabel = typeof projectType === 'object' ? (projectType.label || '') : String(projectType)
      if (typeLabel) {
        const ghlType = resolveGHLProjectType(formType, typeLabel)
        customFields.push({ id: config.customFieldIds.Project_Type, field_value: ghlType })
      }
    }

    // 6. Dedup: check for existing opportunity (same contact + same pipeline + address match)
    const existingOpps = await getContactOpportunities(subAccount, contact.id)
    const existingOpp = existingOpps.find(o =>
      o.pipelineId === config.pipelineId &&
      o.status === 'open' &&
      (o.name || '').toLowerCase().includes(projectAddress.toLowerCase())
    )

    let opportunity
    if (existingOpp) {
      console.log(`[GHL] Found existing opp ${existingOpp.id}, updating instead of duplicate`)
      opportunity = await updateGHLOpportunity(subAccount, {
        opportunityId: existingOpp.id,
        title,
        customFields,
      })
    } else {
      console.log(`[GHL] Creating opportunity in ${subAccount}: "${title}"`)
      opportunity = await createGHLOpportunity(subAccount, {
        title,
        pipelineId: config.pipelineId,
        stageId: config.enConsultaStageId,
        contactId: contact.id,
        customFields,
        assignedTo: formData._landing_timeline ? LANDING_ASSIGNED_USER : undefined,
      })
    }

    console.log(`[GHL] Opportunity created: ${opportunity?.id || 'unknown'}`)

    // 6b. Create contact note with scope of work (landing forms only)
    if (scopeText && contact.id) {
      console.log(`[GHL] Creating contact note with scope text`)
      createGHLContactNote(subAccount, contact.id, `Client: ${scopeText}`)
        .then(r => console.log('[GHL] Contact note created:', r))
        .catch(e => console.warn('[GHL] Contact note failed:', e.message))
    }

    // 7. Apply tags to contact (GHL opportunities don't support tags)
    const tags = resolveTags(formType, formData, utmData)
    if (tags.length) {
      console.log(`[GHL] Tagging contact with: ${tags.join(', ')}`)
      tagGHLContact(subAccount, contact.id, tags).catch(e => console.warn('[GHL] Contact tag failed:', e.message))
    }

    return { contactId: contact.id, opportunityId: opportunity?.id, subAccount }
  } catch (err) {
    // Fire-and-forget: log but never throw
    console.error('[GHL] Sync failed (non-blocking):', err.message || err)
    return null
  }
}

// ── Construction Workflow: project fetch ──────────────────────────────────────

const _conCache = new Map() // key → { data, ts }
const CON_CACHE_TTL = 5 * 60 * 1000 // 5 min

/**
 * Fetch all construction opportunities from GHL (both Ventas + Construcción pipelines).
 * Returns a normalized flat array sorted by updatedAt desc.
 * Cached per session user for 5 minutes. Pass force=true to bypass cache.
 *
 * @param {object|null} session   — Supabase session (for cache key)
 * @param {object}      opts
 * @param {boolean}     opts.force — bypass cache
 * @param {string}      opts.q     — optional search query forwarded to GHL
 * @returns {Promise<Array>}
 */
export async function fetchConstructionProjects(session, { force = false, q = '' } = {}) {
  const uid = session?.user?.id || 'anon'
  const cacheKey = `construction:${uid}:${q}`
  if (!force) {
    const cached = _conCache.get(cacheKey)
    if (cached && Date.now() - cached.ts < CON_CACHE_TTL) return cached.data
  }

  const VENTAS_ID       = 'p2ZEjMort6BjUO1g2RJN'
  const CONSTRUCCION_ID = 'hx4Ipc59bCXOrnpfMyhZ'
  const CON_PIPELINES   = new Set([VENTAS_ID, CONSTRUCCION_ID])

  // GHL /opportunities/search ignores pipelineId as a filter — fetch all (max 100)
  // and filter client-side. 100 covers the full construction sub-account easily.
  const res = await callGHLProxy('list_opportunities', 'construction', {
    limit: 100,
    q: q || undefined,
  })
  const allRaw = res.opportunities || []

  const normalize = (opp) => {
    const rawName = (opp.contact?.name || '').trim()
    // GHL stores phone numbers as contact names when no real name was entered
    const clientName = rawName && /^[\d\s\-()+.]+$/.test(rawName) ? '' : rawName
    /** @type {ConProject} */
    const result = {
      id:            opp.id,
      name:          opp.name || '',
      clientName,
      clientEmail:   (opp.contact?.email || '').toLowerCase().trim(),
      clientPhone:   opp.contact?.phone || rawName || '',  // keep phone; if rawName was phone, use it here
      stageId:       opp.pipelineStageId || '',
      pipelineId:    opp.pipelineId || '',
      status:        opp.status || 'open',
      monetaryValue: opp.monetaryValue || 0,
      updatedAt:     opp.updatedAt || opp.createdAt || '',
      createdAt:     opp.createdAt || '',
      contactId:     opp.contactId || '',
    }
    warnMissingFields(result, _CON_FIELDS, 'ConProject')
    return result
  }

  const all = allRaw
    .filter(opp => CON_PIPELINES.has(opp.pipelineId))
    .map(normalize)
    .sort((a, b) => (b.updatedAt > a.updatedAt ? 1 : -1))

  _conCache.set(cacheKey, { data: all, ts: Date.now() })
  return all
}

export function clearConstructionCache(session) {
  const uid = session?.user?.id || 'anon'
  for (const key of _conCache.keys()) {
    if (key.startsWith(`construction:${uid}`)) _conCache.delete(key)
  }
}
